import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BarChart3, Sparkles, Loader2, ExternalLink, Pencil, Check, X, RefreshCw, Search, AlertTriangle, Zap, TrendingUp, TrendingDown, Minus, FileText, FileType2 } from "lucide-react";
import { kpiCompanies, kpiGet, kpiRun, kpiEdit, kpiSearch, kpiExport, shareCreate, thesisPollJob, KPI_POLL, kpiErrorSeen, kpiFileDownload } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import KpiDocuments from "@/components/kpi/KpiDocuments";
import ShareMenu from "@/components/ShareMenu";
import KpiChat from "@/components/kpi/KpiChat";
import KpiNews from "@/components/kpi/KpiNews";
import HoverTip from "@/components/HoverTip";
import { CompanyLogo, useCompanyIdentity, siteLabel } from "@/components/CompanyIdentity";
import { freshnessInfo, freshnessTip } from "@/lib/freshness";
import { toast } from "sonner";

const COEF_TIP = "Coeficiente de validación (C = 1 + α·S, α=0,5): resume si los KPIs validan la tesis. >1 (verde) se valida, <1 (rojo) se deteriora, ≈1 neutral. La S es la señal agregada (−1 a +1): el promedio, ponderado por la relevancia de cada KPI, de cuánto apoyan (+) o contradicen (−) la tesis. Ej.: S=+0,5 → C=1,25; S=0 → C=1,00; S=−0,5 → C=0,75.";

const coefColor = (c) => {
    if (c == null) return "#7A7A7A";
    if (c >= 1.05) return "#1D7044";
    if (c <= 0.95) return "#B32A22";
    return "#B8860B";
};
const coefLabel = (c) => {
    if (c == null) return "Sin datos";
    if (c >= 1.05) return "Validándose";
    if (c <= 0.95) return "Deteriorándose";
    return "Neutral";
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };

const srcDomain = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; } };
// Kind of a consulted source (list under the KPI table and the note in «Documentos como fuente»).
const srcKind = (url) => {
    const u = String(url || "");
    if (u.startsWith("doc:")) return "doc";
    if (/(^|\.)sec\.gov$/i.test(srcDomain(u))) return "sec";
    return "web";
};
const SRC_KIND_LABEL = { doc: "Documento", sec: "SEC", web: "Web" };
const SRC_KIND_CLASS = {
    doc: "text-[#052049] border-[#052049]/40 bg-[#F4F6FA]",
    sec: "text-[#1D7044] border-[#1D7044]/40 bg-[#EEF7F1]",
    web: "text-[#4A4A4A] border-black/20 bg-white",
};
const srcTitle = (s) => (String(s.title || "").replace(/^\[Documento\]\s*/, "").trim() || srcDomain(s.url) || s.url);
const SOURCES_PREVIEW = 10;

// Trend indicator: compares the latest coefficient with the previous (re)analysis.
const CoefTrend = ({ history }) => {
    if (!history || history.length < 2) return null;
    const last = history[history.length - 1].coef;
    const prev = history[history.length - 2].coef;
    const delta = last - prev;
    const up = delta > 0.005, down = delta < -0.005;
    const color = up ? "#1D7044" : down ? "#B32A22" : "#7A7A7A";
    const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
    const tip = "Evolución del coeficiente entre reanálisis (más reciente abajo):\n\n" +
        history.slice(-8).map((h) => `${h.date}: ${Number(h.coef).toFixed(2)}`).join("\n");
    return (
        <HoverTip text={tip}>
            <span className="inline-flex items-center gap-1 text-xs font-semibold tabular-nums cursor-help" style={{ color }} data-testid="kpi-coef-trend">
                <Icon size={14} /> {delta > 0 ? "+" : ""}{delta.toFixed(2)} vs análisis previo
            </span>
        </HoverTip>
    );
};

const CoefBadge = ({ c, signal, size = "lg" }) => (
    <div className="inline-flex items-center gap-2" data-testid="coef-badge">
        <span
            className={`font-serif tabular-nums font-medium ${size === "lg" ? "text-4xl" : "text-xl"}`}
            style={{ color: coefColor(c) }}
        >
            {c == null ? "—" : c.toFixed(2)}
        </span>
        <span className="text-xs uppercase tracking-[0.12em] font-semibold" style={{ color: coefColor(c) }}>
            {coefLabel(c)}{signal != null ? ` · S=${signal.toFixed(2)}` : ""}
        </span>
    </div>
);

// Needle gauge for the global coefficient. Range [0.5, 1.5] (C = 1 + 0.5·S, S∈[-1,1]),
// centred at 1.0. Styled like the macro indicator. 0.5 = tesis se deteriora (rojo),
// 1.5 = tesis se valida (verde).
const CoefGauge = ({ c }) => {
    const cx = 100, cy = 96, R = 76;
    if (c == null) return null;
    const val = Math.max(0.5, Math.min(1.5, c));
    const rot = (val - 1) * 180;   // 1→0° (up), 0.5→-90° (left), 1.5→+90° (right)
    return (
        <svg viewBox="0 0 200 118" className="w-full max-w-[210px]" data-testid="coef-gauge">
            <defs>
                <linearGradient id="kpiGaugeGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#B32A22" />
                    <stop offset="25%" stopColor="#E8833A" />
                    <stop offset="50%" stopColor="#9AA0A6" />
                    <stop offset="75%" stopColor="#7BC47F" />
                    <stop offset="100%" stopColor="#1F7A3D" />
                </linearGradient>
            </defs>
            <path d={`M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${cx + R} ${cy}`} fill="none" stroke="url(#kpiGaugeGrad)" strokeWidth="13" strokeLinecap="round" />
            <text x={cx - R} y={cy + 15} textAnchor="middle" className="fill-[#B32A22]" fontSize="9" fontWeight="700">0,5 · deteriora</text>
            <text x={cx} y="16" textAnchor="middle" className="fill-[#6A6A6A]" fontSize="9" fontWeight="700">1</text>
            <text x={cx + R} y={cy + 15} textAnchor="middle" className="fill-[#1F7A3D]" fontSize="9" fontWeight="700">1,5 · valida</text>
            <g transform={`rotate(${rot} ${cx} ${cy})`}>
                <line x1={cx} y1={cy} x2={cx} y2={cy - R + 8} stroke="#052049" strokeWidth="3" strokeLinecap="round" />
            </g>
            <circle cx={cx} cy={cy} r="5.5" fill="#052049" />
        </svg>
    );
};

// Big title with the selected company, between the company selector and the first section.
// The name opens the company's website (new tab); the ticker opens its page in the app.
const CompanyTitle = ({ company }) => {
    const identity = useCompanyIdentity(company?.ticker);
    if (!company) return null;
    const site = identity?.website;
    const name = company.name || company.ticker;
    return (
        <div className="flex items-center gap-3 sm:gap-4 mb-5 pb-3 border-b-2 border-[#052049]" data-testid="kpi-company-title">
            <CompanyLogo logoUrl={identity?.logo_url} logoWidth={identity?.logo_width} brandColor={identity?.brand_color} ticker={company.ticker} testid="kpi-company-logo" />
            <div className="min-w-0">
                <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-[#052049] font-semibold [overflow-wrap:anywhere]">
                    {site ? (
                        <HoverTip text={`Abrir la web de la empresa (${siteLabel(site)}) en una pestaña nueva.`}>
                            <a href={site} target="_blank" rel="noopener noreferrer" className="hover:underline decoration-2 underline-offset-4 inline-flex items-baseline gap-2" data-testid="kpi-company-website">
                                <span className="[overflow-wrap:anywhere]">{name}</span>
                                <ExternalLink size={18} className="shrink-0 opacity-50 self-center" aria-hidden />
                            </a>
                        </HoverTip>
                    ) : name}
                </h2>
                <HoverTip text={`Abrir la ficha de valoración de ${name} (${company.ticker}): precio, Ratio Compra/Venta, proyecciones e histórico.`}>
                    <Link to={`/company/${encodeURIComponent(company.ticker)}`} className="overline text-[#7A7A7A] hover:text-[#052049] hover:underline mt-0.5 inline-block" data-testid="kpi-company-ficha-link">
                        {company.ticker} · ver ficha
                    </Link>
                </HoverTip>
            </div>
        </div>
    );
};

export default function Kpis() {
    const { user } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [selId, setSelId] = useState(null);
    // A document source (`doc:<id>`) opens the stored file, which needs the session.
    const openSource = async (e, url) => {
        if (!String(url || "").startsWith("doc:")) return;
        e.preventDefault();
        const w = window.open("", "_blank");
        try {
            const r = await kpiFileDownload(selId, url.slice(4));
            const u = URL.createObjectURL(r.data);
            if (w) w.location.href = u; else window.open(u, "_blank");
            setTimeout(() => URL.revokeObjectURL(u), 60000);
        } catch {
            if (w) w.close();
            toast.error("Ese documento ya no está entre los documentos de la empresa.");
        }
    };
    const [docsRefresh, setDocsRefresh] = useState(0);  // bump → KpiDocuments reloads (show auto-fetched docs after analysis)
    const [newsRefresh, setNewsRefresh] = useState(0);  // bump → KpiNews reloads (show news refreshed during a full Reanalizar)
    const [snap, setSnap] = useState(null);
    const [srcAll, setSrcAll] = useState(false);  // full list of consulted sources
    const showSources = () => {
        setSrcAll(true);
        setTimeout(() => document.getElementById("kpi-sources")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    };
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);   // analysis progress text
    const [editing, setEditing] = useState(false);
    const [stale, setStale] = useState(null);   // { reasons: [] } when reanalyze pending
    const [shared, setShared] = useState(null); // shared reanalysis of the company (services.kpi_packages)
    const [genBlock, setGenBlock] = useState(null); // its thesis is being generated now: no analysis until it ends
    const [lastError, setLastError] = useState(null); // the last analysis failed (maybe while the page was closed)
    const selRef = useRef(null);  // company on screen: a finished analysis of another one doesn't overwrite it
    const [sortMode, setSortMode] = useState("thesis");  // thesis | analysis | alpha | coef

    const sortedCompanies = useMemo(() => {
        const arr = companies.map((c, i) => ({ c, i }));
        if (sortMode === "alpha") {
            arr.sort((a, b) => (a.c.ticker || "").localeCompare(b.c.ticker || "", "es"));
        } else if (sortMode === "coef") {
            // Highest coefficient first; companies without coefficient go last.
            arr.sort((a, b) => {
                const ca = a.c.coef_global, cb = b.c.coef_global;
                if (ca == null && cb == null) return a.i - b.i;
                if (ca == null) return 1;
                if (cb == null) return -1;
                return cb - ca;
            });
        } else if (sortMode === "analysis") {
            // Oldest analysis first; never-analyzed treated as oldest (need attention).
            arr.sort((a, b) => {
                const ta = a.c.kpi_generated_at ? Date.parse(a.c.kpi_generated_at) : 0;
                const tb = b.c.kpi_generated_at ? Date.parse(b.c.kpi_generated_at) : 0;
                return ta - tb;
            });
        } else {
            arr.sort((a, b) => a.i - b.i);  // API order = by thesis generation (default)
        }
        return arr.map((x) => x.c);
    }, [companies, sortMode]);
    // Search box beside the title: filters the company buttons by ticker or name
    // (ignoring accents and case); Enter opens the first match.
    const [companyFilter, setCompanyFilter] = useState("");
    const shownCompanies = useMemo(() => {
        const norm = (x) => (x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        const q = norm(companyFilter.trim());
        if (!q) return sortedCompanies;
        return sortedCompanies.filter((c) => norm(c.ticker).includes(q) || norm(c.name).includes(q));
    }, [sortedCompanies, companyFilter]);
    const [draft, setDraft] = useState([]);
    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [searchNote, setSearchNote] = useState(null);

    const loadCompanies = useCallback(async () => {
        if (!user) { setCompanies([]); return; }
        try {
            const d = await kpiCompanies();
            setCompanies(d.companies || []);
        } catch { setCompanies([]); }
    }, [user]);

    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    const selectCompany = async (id) => {
        selRef.current = id;
        setSelId(id); setSnap(null); setSrcAll(false); setEditing(false); setLoading(true); setStale(null); setStatus(null); setGenBlock(null); setLastError(null);
        setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ir_unread: 0 } : c)));  // opening clears the IR alert
        try {
            const d = await kpiGet(id);
            setSnap(d.kpi_snapshot || null);
            setStale(d.kpi_stale ? { reasons: d.kpi_stale_reasons || [] } : null);
            setShared(d.shared || null);
            setGenBlock(d.thesis_generating || null);
            setLastError(d.last_error || null);
            // An analysis launched earlier is still running (the page was left meanwhile): follow it.
            if (d.running_job?.id) follow(id, d.running_job.id, d.running_job.mode === "incremental");
        } catch { setSnap(null); setShared(null); }
        finally { setLoading(false); }
    };

    // Re-fetch stale flag for the current company (after adding docs/chat/thesis).
    const refreshStale = useCallback(async () => {
        if (!selId) return;
        try {
            const d = await kpiGet(selId);
            setStale(d.kpi_stale ? { reasons: d.kpi_stale_reasons || [] } : null);
            setShared(d.shared || null);
            setGenBlock(d.thesis_generating || null);
        } catch { /* ignore */ }
    }, [selId]);

    // While its thesis is being generated, check every 20 s so the buttons come back when it ends.
    useEffect(() => {
        if (!genBlock) return undefined;
        const t = setInterval(refreshStale, 20000);
        return () => clearInterval(t);
    }, [genBlock, refreshStale]);

    // While another user is reanalysing, check every 20 s so «Actualizar» turns red when it lands.
    useEffect(() => {
        if (!shared?.busy || shared.busy.mine) return undefined;
        const t = setInterval(refreshStale, 20000);
        return () => clearInterval(t);
    }, [shared, refreshStale]);

    // Shared reanalysis rules (same as the shared thesis): «Reanalizar» is refused while another
    // user runs one, while a newer one is pending to apply, and in the 3 days after the last one
    // unless the company has published results since.
    const sharedPending = !!shared?.pending && !!shared?.exists;
    const busyOther = !!shared?.busy && !shared.busy.mine;
    const reanalyzeBlock = genBlock ? genBlock
        : !shared ? null
        : busyOther ? "Otro usuario está reanalizando ahora los KPI de esta empresa. Cuando termine, «Actualizar» se pondrá en rojo y traerá su análisis en unos segundos."
        : sharedPending ? "Hay un reanálisis más reciente de otro usuario sin aplicar: pulsa «Actualizar» primero (unos segundos, casi sin coste)."
        : shared.can_generate === false ? `Reanalizado hace ${shared.age_days ?? 0} día(s): no ha dado tiempo material a que cambie nada, así que nadie puede reanalizar esta empresa hasta dentro de ${shared.wait_days || 1} día${(shared.wait_days || 1) === 1 ? "" : "s"} (antes, solo si publica resultados).`
        : null;
    const updateTip = sharedPending
        ? `Hay un reanálisis compartido de hace ${shared.pending_days ?? 0} d que aún no has aplicado. Actualizar trae sus KPI de fuentes públicas (web, documentos oficiales, noticias), los junta con los de tus documentos, chats y búsquedas, y recalcula el coeficiente con tus tesis. Unos segundos, casi sin coste.`
        : "Incorpora solo los cambios desde el último análisis: noticias refrescadas y documentos añadidos. Rejuzga con el contexto actual sin volver a buscar ni re-parsear documentos. Si otro usuario reanaliza la empresa, aquí se trae su análisis (se pondrá en rojo). Rápido y barato.";
    const reanalyzeTip = reanalyzeBlock || "Reconstrucción completa desde cero: vuelve a buscar fuentes, descarga y re-parsea documentos, refresca noticias y recalcula todo. La parte pública (web, documentos oficiales, noticias) queda disponible para todos los usuarios que siguen la empresa; tus documentos, chats y tesis no se comparten. Más lento y con más coste; tras un reanálisis nadie puede repetirlo en 3 días, salvo que haya resultados.";

    // Wait for an analysis job (just launched, or found running when the page was opened) and
    // show its result; ignored if the user has moved to another company meanwhile.
    const runJob = async (id, incr, start) => {
        const onStatus = (_s, job) => { if (selRef.current === id) setStatus(job?.stage || (incr ? "Actualizando KPIs…" : "Analizando KPIs con IA…")); };
        setStatus(incr ? "Actualizando con los cambios…" : "Buscando resultados y comunicados…"); setLoading(true); setLastError(null);
        try {
            const res = await start(onStatus);
            if (selRef.current !== id) return;
            const result = res?.result || res;
            setSnap(result);
            setStale(null);
            try { setShared((await kpiGet(id)).shared || null); } catch { /* keep */ }
            await loadCompanies();
            setDocsRefresh((n) => n + 1);  // reveal auto-fetched docs (SEC/deck/página) added during the analysis
            if (!incr) setNewsRefresh((n) => n + 1);  // reveal news refreshed during a full Reanalizar
            toast.success(incr ? "KPIs actualizados" : "KPIs analizados");
        } catch (e) {
            if (selRef.current === id) toast.error(e?.response?.data?.detail || "Error analizando KPIs");
        } finally {
            if (selRef.current === id) { setStatus(null); setLoading(false); }
        }
    };
    const analyze = (mode = "full") => {
        if (!selId) return;
        const id = selId;
        return runJob(id, mode === "incremental", (onStatus) => kpiRun(id, onStatus, mode));
    };
    const closeError = () => {
        setLastError(null);
        if (selId) kpiErrorSeen(selId).catch(() => { /* ignore */ });
    };
    const follow = (id, jobId, incr) => runJob(id, incr, (onStatus) => thesisPollJob(jobId, onStatus, KPI_POLL));

    const startEdit = () => { setDraft((snap.kpis || []).map((k) => ({ ...k }))); setEditing(true); };    const setField = (i, field, val) => setDraft((d) => d.map((k, j) => (j === i ? { ...k, [field]: val } : k)));
    const saveEdit = async () => {
        setLoading(true);
        try {
            const payload = draft.map((k) => ({
                ...k,
                signal: k.signal === "" || k.signal == null ? null : Number(k.signal),
                weight: k.weight === "" || k.weight == null ? null : Number(k.weight),
            }));
            const d = await kpiEdit(selId, payload);
            setSnap(d.kpi_snapshot);
            setEditing(false);
            toast.success("Coeficiente recalculado");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally { setLoading(false); }
    };

    const rows = editing ? draft : (snap?.kpis || []);
    const selCompany = companies.find((c) => c.id === selId);

    const [kpiDl, setKpiDl] = useState(null); // "pdf" | "docx" | null
    const downloadKpis = async (fmt) => {
        if (!selId) return;
        setKpiDl(fmt);
        try {
            const res = await kpiExport(selId, fmt);
            let fname = `KPIs ${selCompany?.ticker || "empresa"}.${fmt}`;
            const disp = res.headers?.["content-disposition"];
            const m = disp && /filename\*=UTF-8''([^;]+)/i.exec(disp);
            if (m) { try { fname = decodeURIComponent(m[1]); } catch { /* keep fallback */ } }
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = fname;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo descargar el documento KPI");
        } finally { setKpiDl(null); }
    };

    const doSearch = async () => {
        const q = query.trim();
        if (!q || searching) return;
        setSearching(true); setSearchNote(null);
        try {
            const res = await kpiSearch(selId, q);
            const result = res?.result || res;
            setSnap(result);
            setQuery("");
            if (result?.search_note) { setSearchNote(result.search_note); toast.info(result.search_note); }
            else { toast.success("KPI añadido · coeficiente actualizado"); await loadCompanies(); }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error buscando el dato");
        } finally { setSearching(false); }
    };

    if (!user) {
        return (
            <div data-testid="kpis-page">
                <div className="overline text-[#B32A22] mb-2">Validación operativa</div>
                <h1 className="font-serif text-4xl mb-4">
                    <HoverTip text="KPIs = Indicadores Clave de Rendimiento (Key Performance Indicators): las métricas operativas más importantes de cada empresa (ARR, NRR, clientes, backlog, suscriptores…) que usamos para validar tu tesis.">
                        <span className="cursor-help" data-testid="kpis-title-tip-guest">KPIs</span>
                    </HoverTip>
                </h1>
                <div className="border border-black bg-white p-8 text-center text-[#4A4A4A]">
                    Inicia sesión con Google para validar tus tesis con los KPIs operativos de cada empresa.
                </div>
            </div>
        );
    }

    return (
        <div data-testid="kpis-page">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                <div className="min-w-0 w-full sm:w-auto">
                    <div className="overline text-[#B32A22] mb-1">Validación operativa de la tesis</div>
                    <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
                        <h1 className="font-serif text-4xl leading-none flex items-center gap-2">
                            <BarChart3 size={30} />
                            <HoverTip text="KPIs = Indicadores Clave de Rendimiento (Key Performance Indicators): las métricas operativas más importantes de cada empresa (ARR, NRR, clientes, backlog, suscriptores…) que usamos para validar tu tesis.">
                                <span className="cursor-help" data-testid="kpis-title-tip">KPIs</span>
                            </HoverTip>
                        </h1>
                        <label className="relative w-full sm:w-64" data-testid="kpi-company-search">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7A7A7A] pointer-events-none" />
                            <input type="search" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && shownCompanies[0] && shownCompanies[0].id !== selId) selectCompany(shownCompanies[0].id);
                                    if (e.key === "Escape") setCompanyFilter("");
                                }}
                                placeholder="Buscar empresa (ticker o nombre)"
                                aria-label="Buscar empresa en tus KPIs"
                                className="w-full border border-black bg-white pl-8 pr-2 py-1.5 text-sm placeholder:text-[#9A9A9A] focus:outline-none focus:ring-1 focus:ring-black"
                                data-testid="kpi-company-search-input" />
                        </label>
                    </div>
                </div>
            </div>
            <p className="text-sm text-[#4A4A4A] max-w-2xl mb-6">
                Extrae por IA los KPIs operativos (ARR, NRR, clientes, backlog, suscriptores…) de los últimos resultados y calcula un
                <strong> coeficiente de validación</strong>: <span className="text-[#1D7044] font-semibold">&gt;1 la tesis se valida</span>,
                <span className="text-[#B32A22] font-semibold"> &lt;1 se deteriora</span>.
            </p>

            {/* Company selector */}
            <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[11px]" data-testid="kpi-sort-controls">
                <span className="uppercase tracking-wider text-[#7A7A7A] mr-1">Ordenar:</span>
                {[
                    { k: "analysis", label: "Antigüedad de análisis", tip: "Ordena por cuándo se analizó o reanalizó por última vez (lo más antiguo —o sin analizar— primero; lo que más conviene actualizar)." },
                    { k: "alpha", label: "Alfabético", tip: "Ordena las empresas por ticker, de la A a la Z." },
                    { k: "coef", label: "Por coeficiente", tip: "Ordena por el coeficiente de validación, de mayor a menor (las tesis que más se están validando primero; las que se deterioran al final). Las empresas sin coeficiente calculado quedan al final." },
                    { k: "thesis", label: "Por tesis", tip: "Orden por defecto: por antigüedad del plan de tesis. Una empresa aparece aquí cuando desarrollas su plan de tesis; se listan en el orden en que fuiste creando esos planes en tu cuenta (igual que en la página de Tesis)." },
                ].map((o) => (
                    <HoverTip key={o.k} text={o.tip}>
                        <button onClick={() => setSortMode(o.k)}
                            className={`px-2 py-0.5 border transition-colors ${sortMode === o.k ? "bg-black text-[#FDF1E6] border-black" : "bg-white text-black border-black/30 hover:border-black"}`}
                            data-testid={`kpi-sort-${o.k}`}>{o.label}</button>
                    </HoverTip>
                ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="kpi-company-selector">
                {shownCompanies.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => selectCompany(c.id)}
                        title={c.name || undefined}
                        className={`text-xs font-semibold px-3 py-1.5 border transition-colors flex flex-col items-start gap-0.5 ${selId === c.id ? "bg-black text-[#FDF1E6] border-black" : "bg-white text-black border-black/30 hover:border-black"}`}
                        data-testid={`kpi-company-${c.ticker}`}
                    >
                        <span className="flex items-center gap-1.5">
                            {c.ticker}
                            {c.ir_unread > 0 && (
                                <HoverTip text={`${c.ir_unread} novedad${c.ir_unread === 1 ? "" : "es"} de Investor Relations (IR) sin revisar. Ábrela para verlas y reanaliza si procede.`}>
                                    <span className="inline-block w-2 h-2 rounded-full animate-pulse cursor-help" style={{ backgroundColor: selId === c.id ? "#FBBF24" : "#B8860B" }} data-testid={`kpi-ir-unread-${c.ticker}`} />
                                </HoverTip>
                            )}
                            {c.has_kpis && c.coef_global != null && (
                                <span className="tabular-nums" style={{ color: selId === c.id ? "#fff" : coefColor(c.coef_global) }}>· {c.coef_global.toFixed(2)}</span>
                            )}
                        </span>
                        {c.has_kpis && (() => {
                            const info = freshnessInfo(c.kpi_generated_at, c.last_earnings_date, c.next_earnings_date);
                            if (!info) return null;
                            const col = info.stale ? (selId === c.id ? "#FCA5A5" : "#B32A22") : (selId === c.id ? "#FFFFFF" : "#000000");
                            return (
                                <span className="text-[11px] font-normal tabular-nums" style={{ color: col, fontWeight: info.stale ? 700 : 400 }}
                                    title={freshnessTip(info, "la última actualización del KPI")} data-testid={`kpi-fresh-${c.ticker}`}>
                                    hace {info.days}d
                                </span>
                            );
                        })()}
                    </button>
                ))}
                {companies.length === 0 && <span className="text-sm text-[#4A4A4A]">No tienes empresas con tesis desarrollada todavía.</span>}
                {companies.length > 0 && shownCompanies.length === 0 && (
                    <span className="text-sm text-[#4A4A4A]" data-testid="kpi-company-search-empty">
                        Ninguna de tus empresas coincide con «{companyFilter.trim()}». <button onClick={() => setCompanyFilter("")} className="underline hover:text-black">Ver todas</button>
                    </span>
                )}
            </div>

            {!selId && <div className="text-sm text-[#4A4A4A] border border-dashed border-black/30 p-6 text-center">Elige una empresa para empezar.</div>}

            {/* Selected company title (logo + name in the brand serif) */}
            {selId && <CompanyTitle company={selCompany} />}

            {/* Document sources (available before & after analysis) */}
            {selId && <KpiDocuments companyId={selId} refreshKey={docsRefresh} onChanged={refreshStale}
                otherSources={(snap?.sources || []).filter((x) => srcKind(x.url) !== "doc")} onShowSources={showSources} />}

            {/* Conversational KPI analyst — save chat as a document that feeds the coefficient */}
            {selId && <KpiChat companyId={selId} onSaved={() => setDocsRefresh((n) => n + 1)} onChanged={refreshStale} />}

            {/* Qualitative news (informs scores; capped to the 15 most recent) */}
            {selId && <KpiNews companyId={selId} refreshKey={newsRefresh} onChanged={refreshStale} />}

            {/* The last analysis failed (also when it happened with the page closed) */}
            {selId && lastError && !status && (
                <div className="border-2 border-[#B32A22] bg-[#FDECEA] px-3 py-2.5 mb-4 flex items-start justify-between gap-3" data-testid="kpi-last-error">
                    <div className="text-sm text-[#7a1c16] flex items-start gap-2 min-w-0">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[#B32A22]" />
                        <span className="break-words">
                            El último análisis de KPI{lastError.at ? ` (${fmtDate(lastError.at)})` : ""} no terminó: {lastError.error || "error desconocido"}. Los documentos y noticias que llegó a descargar se conservan; vuelve a lanzarlo.
                        </span>
                    </div>
                    <button onClick={closeError} className="btn-ghost !py-1 !px-2.5 text-xs shrink-0" data-testid="kpi-last-error-close">Cerrar</button>
                </div>
            )}

            {/* Its thesis is being generated: no analysis until it ends */}
            {selId && genBlock && !status && (
                <div className="border-2 border-[#B8860B] bg-[#FFF8E6] px-3 py-2.5 mb-4 flex items-start gap-2 text-sm text-[#7a5c00]" data-testid="kpi-gen-block">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[#B8860B]" />
                    <span>{genBlock}</span>
                </div>
            )}

            {/* Pending-reanalyze banner (new docs, chat, thesis, news…) */}
            {selId && stale && snap && !status && (
                <div className="border-2 border-[#B8860B] bg-[#FFF8E6] px-3 py-2.5 mb-4 flex items-start justify-between gap-3" data-testid="kpi-stale-banner">
                    <div className="text-sm text-[#7a5c00] flex items-start gap-2">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[#B8860B]" />
                        <span>
                            Hay cambios sin reflejar en el coeficiente{stale.reasons?.length ? ` (${stale.reasons.join(", ")})` : ""}. Reanaliza para actualizarlo.
                        </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => analyze("full")} disabled={loading || !!reanalyzeBlock} className="btn-ghost !py-1 !px-2.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="kpi-stale-reanalyze" title={reanalyzeBlock || "Reanálisis completo (vuelve a leer todo)"}>
                            <RefreshCw size={13} /> Reanalizar todo
                        </button>
                        <button onClick={() => analyze("incremental")} disabled={loading || !!genBlock} className="btn-primary !py-1 !px-2.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="kpi-stale-incremental" title="Recalcula solo con los cambios nuevos (más rápido y barato)">
                            <Zap size={13} /> Actualizar solo con los cambios
                        </button>
                    </div>
                </div>
            )}

            {selId && loading && !status && (
                <div className="flex items-center gap-2 text-[#4A4A4A] py-8 justify-center"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
            )}

            {selId && status && (
                <div className="border border-black bg-white p-8 text-center" data-testid="kpi-analyzing">
                    <Loader2 className="animate-spin mx-auto mb-3 text-[#B32A22]" size={28} />
                    <div className="font-serif text-lg">{selCompany?.name}</div>
                    <div className="text-sm text-[#4A4A4A] mt-1">{status}</div>
                    <div className="text-xs text-[#7A7A7A] mt-2 max-w-md mx-auto">Puede tardar varios minutos (lee páginas y documentos en vivo + IA). Puedes salir de la página o mirar otra empresa: el análisis sigue y, al volver a esta, verás cómo va.</div>
                </div>
            )}

            {/* No snapshot yet → analyze CTA */}
            {selId && !loading && !status && !snap && (
                <div className="border border-black bg-white p-8 text-center" data-testid="kpi-empty">
                    <div className="font-serif text-xl mb-1">{selCompany?.name}</div>
                    <p className="text-sm text-[#4A4A4A] mb-4">Aún no se han analizado los KPIs operativos de esta empresa{sharedPending ? " en tu cuenta, pero hay un análisis compartido de otro usuario: tráelo en unos segundos" : ""}.</p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        {sharedPending && (
                            <button onClick={() => analyze("incremental")} disabled={!!genBlock} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#B32A22] hover:bg-[#962018] disabled:opacity-40" data-testid="kpi-empty-update-btn" title={updateTip}>
                                <Zap size={15} /> Actualizar · {shared.pending_days ?? 0} d
                            </button>
                        )}
                        <button onClick={() => analyze("full")} disabled={!!reanalyzeBlock} title={reanalyzeBlock || undefined} className={`${sharedPending ? "btn-ghost" : "btn-primary"} inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed`} data-testid="kpi-analyze-btn">
                            <Sparkles size={15} /> Analizar KPIs
                        </button>
                    </div>
                    {reanalyzeBlock && <p className="text-xs text-[#7A7A7A] mt-3 max-w-md mx-auto">{reanalyzeBlock}</p>}
                </div>
            )}

            {/* Snapshot */}
            {selId && !status && snap && (
                <div data-testid="kpi-result">
                    {/* Global coefficient header */}
                    <div className="border border-black bg-white p-5 mb-4 flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <div className="overline text-[#4A4A4A] mb-1">Coeficiente global · {selCompany?.name}</div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <CoefBadge c={snap.coef_global} signal={snap.signal_global} />
                                <CoefTrend history={snap.coef_history} />
                            </div>
                            <div className="text-xs text-[#7A7A7A] mt-1">
                                {snap.period ? `Periodo: ${snap.period} · ` : ""}{snap.generated_at ? `Analizado ${fmtDate(snap.generated_at)}` : ""}{snap.package_version ? " · fuentes públicas compartidas + las tuyas" : ""}{snap.edited_at ? " · editado" : ""}
                            </div>
                            {busyOther && <div className="text-xs text-[#7a5a10] mt-1 max-w-xl" data-testid="kpi-shared-busy">{reanalyzeBlock}</div>}
                            {(() => {
                                const uncovered = (snap.drivers || []).filter((d) => !d.n_kpis);
                                if (!uncovered.length) return null;
                                return (
                                    <div className="mt-2 flex items-start gap-1.5 text-[12px] bg-[#FBF3E0] border border-[#B8860B]/50 px-2.5 py-1.5 max-w-xl" data-testid="kpi-uncovered-warning">
                                        <AlertTriangle size={14} className="text-[#B8860B] shrink-0 mt-0.5" />
                                        <div className="text-[#7a5a10] leading-snug">
                                            {uncovered.length === 1 ? "1 área de la tesis sin KPIs" : `${uncovered.length} áreas de la tesis sin KPIs`} (<strong>{uncovered.map((d) => d.name).join(", ")}</strong>): el coeficiente solo refleja las áreas con datos. Busca o desarrolla KPIs de esas áreas para una validación más fiable y completa.
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                        <div className="shrink-0" data-testid="coef-gauge-wrap">
                            <CoefGauge c={snap.coef_global} />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {!editing && (
                                <>
                                    <HoverTip text={updateTip}>
                                        <button onClick={() => analyze("incremental")} disabled={!!genBlock}
                                                className={"disabled:opacity-40 disabled:cursor-not-allowed " + (sharedPending ? "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.1em] font-semibold text-white bg-[#B32A22] hover:bg-[#962018]" : "btn-ghost inline-flex items-center gap-1.5")}
                                                data-testid="kpi-update-btn">
                                            <Zap size={13} /> Actualizar{sharedPending ? ` · ${shared.pending_days ?? 0} d` : ""}
                                        </button>
                                    </HoverTip>
                                    <HoverTip text={reanalyzeTip}>
                                        <button onClick={() => analyze("full")} disabled={!!reanalyzeBlock} className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="kpi-reanalyze-btn"><RefreshCw size={13} /> Reanalizar</button>
                                    </HoverTip>
                                    <span className="w-px h-5 bg-black/15 mx-0.5" aria-hidden />
                                    <ShareMenu
                                        size="sm"
                                        title={`KPIs ${selCompany?.ticker || ""}`.trim()}
                                        testidPrefix="kpi-snapshot-share"
                                        createShare={() => shareCreate({ kind: "kpi_snapshot", company_id: selId, fmt: "pdf" })}
                                    />
                                    <HoverTip text="Descarga esta validación de KPIs (coeficiente, drivers y tabla) en PDF.">
                                        <button onClick={() => downloadKpis("pdf")} disabled={!!kpiDl} className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="kpi-download-pdf">
                                            {kpiDl === "pdf" ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} PDF
                                        </button>
                                    </HoverTip>
                                    <HoverTip text="Descarga esta validación de KPIs en Word (.docx, compatible con Google Docs).">
                                        <button onClick={() => downloadKpis("docx")} disabled={!!kpiDl} className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="kpi-download-docx">
                                            {kpiDl === "docx" ? <Loader2 size={13} className="animate-spin" /> : <FileType2 size={13} />} Word
                                        </button>
                                    </HoverTip>
                                </>
                            )}
                        </div>
                    </div>

                    {snap.no_source_doc && (
                        <div className="border border-[#B32A22]/50 bg-[#FBE9E7] p-3 mb-4 flex items-start gap-2 max-w-3xl" data-testid="kpi-no-source-warning">
                            <AlertTriangle size={16} className="text-[#B32A22] shrink-0 mt-0.5" />
                            <div className="text-[13px] text-[#8a2318] leading-snug">
                                No se encontró ningún <strong>informe o deck oficial</strong> de {selCompany?.name} para descargar automáticamente, así que el coeficiente se ha calculado solo con búsquedas web (menos fiable).
                                Para una validación robusta, <strong>sube el PDF de resultados</strong> o <strong>pega el transcript</strong> de la llamada en la sección «Documentos como fuente» de abajo y pulsa <em>Actualizar</em>.
                            </div>
                        </div>
                    )}

                    {snap.news_refresh_error && (
                        <div className="border border-[#B8860B]/50 bg-[#FBF3E0] p-3 mb-4 flex items-start gap-2 max-w-3xl" data-testid="kpi-news-refresh-error">
                            <AlertTriangle size={16} className="text-[#B8860B] shrink-0 mt-0.5" />
                            <div className="text-[13px] text-[#7a5a10] leading-snug">
                                No se pudieron buscar noticias nuevas en este análisis (fallo de búsqueda o de la IA); se han mantenido las que ya había guardadas. Prueba con «Buscar noticias» en la sección de noticias de abajo.
                            </div>
                        </div>
                    )}

                    {snap.note && <div className="border border-[#B8860B]/50 bg-[#FBF3E0] p-3 text-sm text-[#7a5a10] mb-4">{snap.note}</div>}

                    {/* Targeted KPI search — adds a specific datapoint and recomputes the coefficient */}
                    <div className="border border-[#052049]/30 bg-[#F4F6FA] p-3 mb-4" data-testid="kpi-search-box">
                        <div className="overline text-[#052049] mb-1.5 flex items-center gap-1.5"><Search size={13} /> Buscar un KPI específico</div>
                        <div className="flex items-center gap-2">
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
                                placeholder="p. ej. número de suscriptores, ARR, backlog…"
                                className="flex-1 border border-black/30 bg-white px-3 py-1.5 text-sm outline-none focus:border-black"
                                disabled={searching}
                                data-testid="kpi-search-input"
                            />
                            <button onClick={doSearch} disabled={searching || !query.trim()} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="kpi-search-btn">
                                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                                {searching ? "Buscando…" : "Buscar y añadir"}
                            </button>
                        </div>
                        <p className="text-[11px] text-[#4A4A4A] mt-1.5">El dato se extrae por IA (con su fuente), se incorpora al cálculo y <strong>actualiza el coeficiente</strong>.</p>
                        {searchNote && <div className="text-xs text-[#B32A22] mt-1.5">{searchNote}</div>}
                    </div>

                    {/* Per-driver coefficients */}
                    {snap.drivers?.length > 0 && (
                        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }} data-testid="kpi-drivers">
                            {snap.drivers.map((d, i) => (
                                <div key={i} className="border border-black/20 bg-white p-3" data-testid={`kpi-driver-${i}`}>
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className="text-sm font-semibold leading-tight">{d.name}</div>
                                        <CoefBadge c={d.coef} size="sm" />
                                    </div>
                                    <div className="text-[11px] text-[#7A7A7A] mb-1">{d.n_kpis} KPI{d.n_kpis === 1 ? "" : "s"}</div>
                                    {d.verdict && <p className="text-xs text-[#3A3A3A] leading-relaxed">{d.verdict}</p>}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* KPI table */}
                    {rows.length > 0 && (
                        <div className="border border-black bg-white overflow-x-auto" data-testid="kpi-table">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-black text-left overline text-[#4A4A4A]">
                                        <th className="p-2">
                                            <div className="flex items-center gap-2">
                                                <span>KPI</span>
                                                {!editing ? (
                                                    <button onClick={startEdit} className="btn-ghost inline-flex items-center gap-1 !py-0.5 !px-1.5 normal-case text-xs" data-testid="kpi-edit-btn"><Pencil size={12} /> Editar</button>
                                                ) : (
                                                    <>
                                                        <button onClick={() => setEditing(false)} className="btn-ghost inline-flex items-center gap-1 !py-0.5 !px-1.5 normal-case text-xs" data-testid="kpi-cancel-edit"><X size={12} /> Cancelar</button>
                                                        <button onClick={saveEdit} className="btn-primary inline-flex items-center gap-1 !py-0.5 !px-1.5 normal-case text-xs" data-testid="kpi-save-edit"><Check size={12} /> Guardar</button>
                                                    </>
                                                )}
                                            </div>
                                        </th>
                                        <th className="p-2 text-right">Actual</th>
                                        <th className="p-2 text-right">Anterior</th>
                                        <th className="p-2 text-right">YoY</th>
                                        <th className="p-2 text-right">Señal</th>
                                        <th className="p-2 text-right">Peso</th>
                                        <th className="p-2">Driver</th>
                                        <th className="p-2">Fuente</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((k, i) => (
                                        <tr key={i} className="border-b border-black/10 align-top" data-testid={`kpi-row-${i}`}>
                                            <td className="p-2">
                                                <div className="font-medium">{k.name}</div>
                                                {k.rationale && !editing && <div className="text-[11px] text-[#7A7A7A] leading-snug mt-0.5">{k.rationale}</div>}
                                            </td>
                                            <td className="p-2 text-right tabular-nums">
                                                {editing
                                                    ? <input value={k.value_current ?? ""} onChange={(e) => setField(i, "value_current", e.target.value)} className="w-20 text-right border border-black/30 px-1 py-0.5" data-testid={`kpi-edit-val-${i}`} />
                                                    : <span className="font-semibold">{k.value_current ?? "—"}</span>}
                                            </td>
                                            <td className="p-2 text-right tabular-nums text-[#7A7A7A]">
                                                {editing
                                                    ? <input value={k.value_prior ?? ""} onChange={(e) => setField(i, "value_prior", e.target.value)} className="w-20 text-right border border-black/30 px-1 py-0.5" />
                                                    : (k.value_prior ?? "—")}
                                            </td>
                                            <td className="p-2 text-right tabular-nums text-xs">{k.yoy_change ?? "—"}</td>
                                            <td className="p-2 text-right tabular-nums" style={{ color: !editing ? (k.signal > 0 ? "#1D7044" : k.signal < 0 ? "#B32A22" : "#7A7A7A") : undefined }}>
                                                {editing
                                                    ? <input type="number" step="0.1" min="-1" max="1" value={k.signal ?? ""} onChange={(e) => setField(i, "signal", e.target.value)} className="w-16 text-right border border-black/30 px-1 py-0.5" data-testid={`kpi-edit-signal-${i}`} />
                                                    : (k.signal != null ? Number(k.signal).toFixed(2) : "—")}
                                            </td>
                                            <td className="p-2 text-right tabular-nums">
                                                {editing
                                                    ? <input type="number" step="0.1" min="0" max="1" value={k.weight ?? ""} onChange={(e) => setField(i, "weight", e.target.value)} className="w-16 text-right border border-black/30 px-1 py-0.5" data-testid={`kpi-edit-weight-${i}`} />
                                                    : (k.weight != null ? Number(k.weight).toFixed(2) : "—")}
                                            </td>
                                            <td className="p-2 text-xs text-[#4A4A4A]">{k.driver}</td>
                                            <td className="p-2">
                                                {k.source_url
                                                    ? <HoverTip text={`${k.source_quote || "Sin cita textual disponible"}${srcDomain(k.source_url) ? `\n\nFuente: ${srcDomain(k.source_url)}` : ""}`} maxWidth={340}>
                                                        <a href={k.source_url} onClick={(e) => openSource(e, k.source_url)} target="_blank" rel="noreferrer" className="text-[#052049] inline-flex items-center gap-0.5 text-xs hover:underline cursor-help" data-testid={`kpi-source-${i}`}><ExternalLink size={12} /> ver</a>
                                                    </HoverTip>
                                                    : (k.source_quote
                                                        ? <HoverTip text={k.source_quote} maxWidth={340}><span className="text-[#7A7A7A] text-xs cursor-help border-b border-dotted border-[#7A7A7A]" data-testid={`kpi-source-${i}`}>cita</span></HoverTip>
                                                        : <span className="text-[#9A9A9A] text-xs">—</span>)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Sources: numbered list (title, kind and link) */}
                    {snap.sources?.length > 0 && (
                        <div id="kpi-sources" className="mt-4 border-t border-black/10 pt-3 scroll-mt-32" data-testid="kpi-sources">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <span className="overline text-[#4A4A4A]">Fuentes consultadas ({snap.sources.length})</span>
                                <HoverTip text="Todo lo que la IA leyó en este análisis para extraer y juzgar los KPI: los documentos de la empresa (los del recuadro «Documentos como fuente», que se abren con tu sesión), el comunicado oficial de resultados presentado a la SEC y los resultados de la búsqueda web (cifras del trimestre, llamada de resultados, perspectivas y noticias). El enlace «ver» de cada KPI apunta a una de ellas." maxWidth={320}>
                                    <span className="text-[#9A9A9A] cursor-help text-xs">ⓘ</span>
                                </HoverTip>
                            </div>
                            <ol className="space-y-1 text-xs">
                                {(srcAll ? snap.sources : snap.sources.slice(0, SOURCES_PREVIEW)).map((s, i) => {
                                    const kind = srcKind(s.url);
                                    const dom = kind === "doc" ? "abrir documento" : srcDomain(s.url);
                                    return (
                                        <li key={i} className="flex items-start gap-2 min-w-0" data-testid={`kpi-src-${i}`}>
                                            <span className="text-[#9A9A9A] tabular-nums w-6 shrink-0 text-right">{i + 1}.</span>
                                            <span className={`shrink-0 border px-1 text-[10px] leading-4 font-semibold ${SRC_KIND_CLASS[kind]}`}>{SRC_KIND_LABEL[kind]}</span>
                                            <span className="min-w-0 flex-1">
                                                <span className="text-[#2A2A2A] break-words">{srcTitle(s)}</span>
                                                {" "}
                                                <a href={s.url} onClick={(e) => openSource(e, s.url)} target="_blank" rel="noreferrer"
                                                   className="text-[#052049] hover:underline inline-flex items-center gap-0.5 whitespace-nowrap" data-testid={`kpi-src-link-${i}`}>
                                                    {kind === "doc" ? <FileText size={10} /> : <ExternalLink size={10} />} {dom}
                                                </a>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                            {snap.sources.length > SOURCES_PREVIEW && (
                                <button onClick={() => setSrcAll((v) => !v)} className="mt-1.5 text-xs text-[#052049] hover:underline" data-testid="kpi-src-more">
                                    {srcAll ? "Ver menos" : `Ver las ${snap.sources.length - SOURCES_PREVIEW} restantes`}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
